import { areaDefinition, FilterSpec, StaticOption } from 'modules/arena/reports/model/areas';
import { addDays, daysInclusive, formatDate, formatPeriod, isValidIsoDate, startOfMonth } from 'modules/arena/reports/model/dates';
import {
  CatalogOption,
  CommonFilters,
  IsoDate,
  PeriodPreset,
  ReportArea,
  ReportCatalog,
  ReportFilters,
  SourceCapabilities,
  SpecificFilters,
} from 'modules/arena/reports/model/types';

export const PERIOD_PRESETS: { id: PeriodPreset; label: string }[] = [
  { id: 'hoje', label: 'Hoje' },
  { id: 'ontem', label: 'Ontem' },
  { id: '7d', label: 'Últimos 7 dias' },
  { id: '30d', label: 'Últimos 30 dias' },
  { id: 'mes-atual', label: 'Mês atual' },
  { id: 'personalizado', label: 'Personalizado' },
];

/** Sem período padrão definido no projeto, o relatório abre no mês atual. */
export const DEFAULT_PRESET: PeriodPreset = '30d';

export function resolvePreset(preset: PeriodPreset, today: IsoDate): { start: IsoDate; end: IsoDate } | null {
  switch (preset) {
    case 'hoje':
      return { start: today, end: today };
    case 'ontem':
      return { start: addDays(today, -1), end: addDays(today, -1) };
    case '7d':
      return { start: addDays(today, -6), end: today };
    case '30d':
      return { start: addDays(today, -29), end: today };
    case 'mes-atual':
      return { start: startOfMonth(today), end: today };
    default:
      return null;
  }
}

export function defaultCommon(today: IsoDate): CommonFilters {
  const range = resolvePreset(DEFAULT_PRESET, today)!;
  return { preset: DEFAULT_PRESET, startDate: range.start, endDate: range.end, unitIds: [], sport: '', grouping: 'dia', compare: true };
}

export function defaultSpecific(area: ReportArea): SpecificFilters {
  const specific: SpecificFilters = {};
  areaDefinition(area).filters.forEach((spec) => {
    specific[spec.key] = spec.defaultValue ?? '';
  });
  return specific;
}

export function defaultFilters(area: ReportArea, today: IsoDate): ReportFilters {
  return { common: defaultCommon(today), specific: defaultSpecific(area) };
}

/** Limites aceitos pela integração, em texto para o usuário. */
export function describeRangeLimits(capabilities: SourceCapabilities, today: IsoDate): string {
  return `Datas entre ${formatDate(capabilities.minDate)} e ${formatDate(today)}, com até ${capabilities.maxRangeDays} dias por consulta.`;
}

/** Erro do intervalo de datas, ou null quando válido. */
export function validateRange(common: CommonFilters, capabilities: SourceCapabilities, today: IsoDate): string | null {
  const { startDate, endDate } = common;
  if (!isValidIsoDate(startDate) || !isValidIsoDate(endDate)) return 'Informe data inicial e final válidas.';
  if (startDate > endDate) return 'A data inicial precisa ser anterior ou igual à data final.';
  if (startDate < capabilities.minDate) return `Não há dados antes de ${formatDate(capabilities.minDate)}.`;
  if (endDate > today) return 'A data final não pode ser posterior a hoje.';
  if (daysInclusive(startDate, endDate) > capabilities.maxRangeDays) {
    return `O intervalo máximo é de ${capabilities.maxRangeDays} dias.`;
  }
  return null;
}

/** Opções de um filtro específico, já restritas pelos filtros dos quais ele depende. */
export function optionsFor(spec: FilterSpec, filters: ReportFilters, catalog: ReportCatalog): StaticOption[] {
  if (spec.options) return spec.options;
  const all: CatalogOption[] = catalog.options[spec.catalogKey ?? ''] ?? [];
  const { sport, unitIds } = filters.common;
  const parentValue = spec.parentKey ? filters.specific[spec.parentKey] : '';
  return all.filter(
    (option) =>
      (!sport || !option.sports || option.sports.includes(sport)) &&
      (!unitIds.length || !option.unitId || unitIds.includes(option.unitId)) &&
      (!parentValue || !option.parentId || option.parentId === parentValue)
  );
}

/**
 * Remove seleções que deixaram de ser compatíveis (ex.: trocar o esporte invalida o professor
 * escolhido). Devolve os filtros limpos e as mensagens para avisar o usuário da mudança.
 */
export function sanitizeFilters(
  area: ReportArea,
  filters: ReportFilters,
  catalog: ReportCatalog
): { filters: ReportFilters; notices: string[] } {
  const notices: string[] = [];
  let next = filters;

  const validUnits = filters.common.unitIds.filter((id) => catalog.units.some((unit) => unit.id === id));
  if (validUnits.length !== filters.common.unitIds.length) {
    next = { ...next, common: { ...next.common, unitIds: validUnits } };
    notices.push('Unidades sem acesso foram removidas do filtro.');
  }
  if (filters.common.sport && !catalog.sports.some((sport) => sport.id === filters.common.sport)) {
    next = { ...next, common: { ...next.common, sport: '' } };
    notices.push('O esporte selecionado não é oferecido nesta arena e foi removido.');
  }

  // Ordem do array importa: pais (professor, torneio) são validados antes dos filhos.
  areaDefinition(area).filters.forEach((spec) => {
    const value = next.specific[spec.key];
    if (!value || spec.required) return;
    const options = optionsFor(spec, next, catalog);
    if (!options.some((option) => option.id === value)) {
      const label = (catalog.options[spec.catalogKey ?? ''] ?? spec.options ?? []).find((o) => o.id === value)?.label ?? value;
      next = { ...next, specific: { ...next.specific, [spec.key]: '' } };
      notices.push(`${spec.label} "${label}" foi removido(a) porque não combina com os demais filtros.`);
    }
  });

  return { filters: next, notices };
}

export interface FilterChip {
  id: string;
  label: string;
  /** Remover este chip devolve o filtro ao padrão. */
  removable: boolean;
}

export function periodLabel(common: CommonFilters): string {
  const preset = PERIOD_PRESETS.find((item) => item.id === common.preset);
  const range = formatPeriod(common.startDate, common.endDate);
  return common.preset === 'personalizado' ? range : `${preset?.label} (${range})`;
}

export function buildChips(area: ReportArea, filters: ReportFilters, catalog: ReportCatalog): FilterChip[] {
  const chips: FilterChip[] = [{ id: 'period', label: `Período: ${periodLabel(filters.common)}`, removable: filters.common.preset !== DEFAULT_PRESET }];
  filters.common.unitIds.forEach((id) => {
    chips.push({ id: `unit:${id}`, label: catalog.units.find((unit) => unit.id === id)?.label ?? id, removable: true });
  });
  if (filters.common.sport) {
    chips.push({ id: 'sport', label: catalog.sports.find((s) => s.id === filters.common.sport)?.label ?? filters.common.sport, removable: true });
  }
  areaDefinition(area).filters.forEach((spec) => {
    const value = filters.specific[spec.key];
    if (!value) return;
    const option = (spec.options ?? catalog.options[spec.catalogKey ?? ''] ?? []).find((item) => item.id === value);
    chips.push({ id: `specific:${spec.key}`, label: `${spec.label}: ${option?.label ?? value}`, removable: !spec.required });
  });
  return chips;
}

/** Aplica a remoção de um chip, voltando aquele filtro ao valor padrão. */
export function removeChip(chipId: string, area: ReportArea, filters: ReportFilters, today: IsoDate): ReportFilters {
  if (chipId === 'period') {
    const common = defaultCommon(today);
    return { ...filters, common: { ...filters.common, preset: common.preset, startDate: common.startDate, endDate: common.endDate } };
  }
  if (chipId === 'sport') return { ...filters, common: { ...filters.common, sport: '' } };
  if (chipId.startsWith('unit:')) {
    const unitId = chipId.slice(5);
    return { ...filters, common: { ...filters.common, unitIds: filters.common.unitIds.filter((id) => id !== unitId) } };
  }
  const key = chipId.replace('specific:', '');
  return { ...filters, specific: { ...filters.specific, [key]: defaultSpecific(area)[key] ?? '' } };
}

/** Ao trocar de área, mantém os filtros comuns e descarta os específicos da área anterior. */
export function carryToArea(target: ReportArea, filters: ReportFilters, overrides?: SpecificFilters): ReportFilters {
  return { common: filters.common, specific: { ...defaultSpecific(target), ...overrides } };
}

export function hasActiveFilters(area: ReportArea, filters: ReportFilters): boolean {
  const defaults = defaultSpecific(area);
  return (
    filters.common.preset !== DEFAULT_PRESET ||
    filters.common.unitIds.length > 0 ||
    Boolean(filters.common.sport) ||
    Object.keys(filters.specific).some((key) => filters.specific[key] !== defaults[key])
  );
}
